<script lang="ts">
import { useRouter, type RouteLocationRaw } from 'vue-router';

/** One linked record — a clause or an obligation — as the list draws it. */
export interface GaiaEntityListRow {
  uid: string;
  title: string;
  /** Caps tag before the title (clause category, obligation type). */
  category?: string | null;
  /** Mono reference after the title (clause number). */
  ref?: string | null;
  body?: string | null;
  to: RouteLocationRaw;
}
</script>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import GaiaAddButton from '../../base/GaiaAddButton.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import GaiaPagination from '../../composite/GaiaPagination.vue';
import GaiaTableRowState from '../../composite/GaiaTableRowState.vue';
import { gaiaFonts, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  rows: {
    type: Array as PropType<GaiaEntityListRow[]>,
    required: true,
  },
  /** What a row is, for the copy: "clause", "obligation". */
  noun: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** The row whose unlink is in flight. */
  busyUid: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** Why the caller can't link or unlink; null when they can. */
  writeBlock: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ add: []; remove: [row: GaiaEntityListRow] }>();

const router = useRouter();

const page = ref(1);
const pageSize = ref(10);
/** The row whose ✕ was pressed — its strip replaces the row until answered. */
const confirmingUid = ref<string | null>(null);

// A shorter list (an unlink, a version switch) may no longer reach the page.
watch(
  () => props.rows.length,
  length => {
    const last = Math.max(1, Math.ceil(length / pageSize.value));
    if (page.value > last) page.value = last;
  }
);

const pageRows = computed(() =>
  props.rows.slice((page.value - 1) * pageSize.value, page.value * pageSize.value)
);

function confirmRemove(row: GaiaEntityListRow): void {
  confirmingUid.value = null;
  emit('remove', row);
}

function setPageSize(size: number): void {
  pageSize.value = size;
  page.value = 1;
}

const tagColor = gaiaNeutral.textTertiary;
const tagBorder = gaiaNeutral.border;
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const mutedColor = gaiaNeutral.textMuted;
const shimmerBase = gaiaNeutral.surfaceSoft;
const monoFont = gaiaFonts.mono;
</script>

<template>
  <!-- A Clauses / Obligations tab of the Contract View mock: the file's linked
       records, a page at a time. Unlink answers in the row's own place. -->
  <div class="entity-list">
    <template v-if="props.loading">
      <div v-for="index in 3" :key="index" class="shimmer" />
    </template>

    <gaia-empty-state v-else-if="props.rows.length === 0" :title="`No ${props.noun} linked yet`">
      Linking one records that this {{ props.noun }} appears in the document, so it is reachable
      from either side.
      <template #actions>
        <gaia-add-button
          :subject="props.noun"
          :icon="false"
          :disabled="Boolean(props.writeBlock)"
          :title="props.writeBlock ?? ''"
          @click="emit('add')"
        />
      </template>
    </gaia-empty-state>

    <template v-for="row in pageRows" v-else :key="row.uid">
      <gaia-table-row-state
        v-if="confirmingUid === row.uid"
        variant="confirm"
        confirm-label="Unlink"
        :title="`Unlink “${row.title}”?`"
        @cancel="confirmingUid = null"
        @confirm="confirmRemove(row)"
      >
        The record itself stays — only its link to this file is removed.
      </gaia-table-row-state>

      <gaia-table-row-state v-else-if="props.busyUid === row.uid" variant="busy">
        Unlinking “{{ row.title }}”…
      </gaia-table-row-state>

      <div v-else class="row" @click="router.push(row.to)">
        <n-flex vertical :size="5" :style="{ flex: 1, minWidth: 0 }">
          <n-flex align="center" :size="9">
            <span v-if="row.category" class="category">{{ row.category }}</span>
            <gaia-text size="row" weight="semibold" tone="ink">
              <router-link :to="row.to" style="color: inherit; text-decoration: none" @click.stop>
                {{ row.title }}
              </router-link>
            </gaia-text>
            <gaia-text v-if="row.ref" size="micro" class="ref">{{ row.ref }}</gaia-text>
          </n-flex>
          <gaia-text v-if="row.body" size="control" tone="tertiary" class="body">
            {{ row.body }}
          </gaia-text>
        </n-flex>

        <gaia-delete-button
          intent="remove"
          class="unlink"
          subject="from this file"
          :disabled="Boolean(props.writeBlock)"
          :title="props.writeBlock ?? undefined"
          @click.stop="confirmingUid = row.uid"
        />
      </div>
    </template>

    <div v-if="!props.loading && props.rows.length > 0" class="foot">
      <gaia-pagination
        :page="page"
        :page-size="pageSize"
        :total="props.rows.length"
        :page-sizes="[10, 25, 50]"
        @update:page="(value: number) => (page = value)"
        @update:page-size="setPageSize"
      />
    </div>
  </div>
</template>

<style scoped>
.entity-list {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

/* A caps tag on a row, sized to the line rather than to GaiaTag's chip box. */
.category {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 19px;
  padding: 0 7px;
  border: 1px solid v-bind(tagBorder);
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: v-bind(tagColor);
}

/* References are compared down the list, so they take the mono face. */
.ref {
  flex: none;
  color: v-bind(mutedColor);
  font-family: v-bind(monoFont);
}

.body {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.55;
}

/* Revealed only while the row is pointed at — a list of records shouldn't read
   as a list of unlink buttons. It stays in the DOM, so focus reveals it too. */
.unlink {
  flex: none;
  opacity: 0;
}

.row:hover .unlink,
.unlink:focus-visible {
  opacity: 1;
}

.foot {
  padding: 4px 18px;
}

.shimmer {
  height: 44px;
  margin: 8px 18px;
  border-radius: 9px;
  background: v-bind(shimmerBase);
}
</style>
